import { get } from 'lodash';
import { useMemo } from 'react';
import { debug } from '../../../../utils/debug.ts';
import { type IMaterializedReference } from '../../evaluate/materializeReferencePath.ts';
import { partitionReferences } from '../../evaluate/partitionReferences.ts';
import {
  createEvaluationVariables,
  type IEvaluationVariables,
} from '../../evaluate/variables/IEvaluationVariables.ts';
import { useLiveValidationContext } from './useLiveValidationContext.ts';
import { useWatchFormReferences } from './useWatchFormReferences.tsx';

/**
 * Hook per accedere a un _qualsiasi_ riferimento (`IReference`) a partire
 * da un Widget. In base al tipo di riferimento, il valore può essere
 * recuperato da:
 * - `useWatchFormReferences` per i riferimenti interni al form
 * - `useLiveValidationContext` per i riferimenti al contesto (inclusi i
 *   semantic fields live)
 *
 * Questa logica è sostanzialmente la replica di `parentWhen`, ma per
 * essere utilizzata in `useExpressionValue` ha bisogno di essere
 * collegata al form di RHF.
 *
 * @param references Riferimenti da cui recuperare i valori. Essendo `materialized`,
 *  Non abbiamo bisogno di passare il nome del campo, perché è già calcolato il path
 *  assoluto.
 */
export function useWatchReferences(
  references: IMaterializedReference[],
): IEvaluationVariables {
  const [contextRefs, formRefs] = useMemo(
    () => partitionReferences(references),
    [references],
  );

  // 1. Valori del form (`useWatch`)
  const formValues = useWatchFormReferences(formRefs);

  // 2. Valori del contesto — gestisce la reattività live dei semantic fields
  const validationContext = useLiveValidationContext(contextRefs);
  const contextValues = useMemo(
    () =>
      contextRefs.map(ref =>
        get(validationContext, ref.absolutePath.substring(1)),
      ),
    [validationContext, contextRefs],
  );

  // Uniamo i valori
  return useMemo(() => {
    // biome-ignore format: <tbd>
    debug.log('expression.watch', 'useWatchReferences', { formRefs, formValues });
    return createEvaluationVariables(
      [...contextRefs, ...formRefs],
      [...contextValues, ...formValues],
    );
  }, [formValues, contextValues, contextRefs, formRefs]);
}
